import type { ReactNode } from "react";
import { css, styled, ThemeProvider } from "styled-components";

import { ImageBackground, UIButton } from "common/components";
import { breakpoints } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getLocalImage } from "domains/createMapDetail/selectors";
import { activateModal } from "domains/modal/actions";
import { darkTheme } from "domains/theme/constants/theme";
import { TopicConnectionBanner } from "domains/topicDetail/components/topicConnectionBanner/TopicConnectionBanner";
import { getTmEditMode, getTmSelectedOuterNode } from "domains/transformationMap/selectors";
import type { Topic } from "entities/topics/types";

const StyledContainer = styled.div<{ isEditing: boolean }>`
  position: relative;
  box-shadow: 0 0 8px ${({ theme }) => theme.shadows.box};
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};

  ${({ isEditing }) =>
    isEditing &&
    css`
      margin: 0 1rem;
      border-radius: 5px;
      overflow: hidden;

      @media ${breakpoints.medium} {
        margin: 0 2rem;
      }
      @media ${breakpoints.large} {
        margin: 0;
      }
    `};
`;

const StyledImage = styled(ImageBackground)<{ advancedMode: boolean; isEditing: boolean }>`
  transition:
    min-height 0.3s,
    max-height 0.3s;
  border-bottom: 1px solid ${({ theme }) => theme.colors.outline};
  width: 100%;
  height: 240px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  ${({ isEditing }) =>
    isEditing &&
    css`
      cursor: pointer;
      transition: background-image 1s ease-in-out;
    `};

  @media ${breakpoints.medium} {
    height: 160px;
  }
`;

const StyledInnerContainer = styled.div<{ hasImage: boolean; isEditing: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  font-size: 1rem;
  padding: 1.8rem 1.5rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  color: #fff;
  background-color: ${({ hasImage }) => `rgba(0, 0, 0, ${hasImage ? 0.5 : 0});`};
  ${({ isEditing }) =>
    isEditing &&
    css`
      pointer-events: none;
    `}

  @media ${breakpoints.large} {
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
  }
`;

const StyledCentralContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  height: 100%;
`;

type ImageSectionProps = { overlayElement: ReactNode; topic?: Topic };

export const ImageSection = ({ topic, overlayElement }: ImageSectionProps) => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const localImage = useSelector(getLocalImage);
  const isEditing = useSelector(getTmEditMode);
  const outerNode = useSelector(getTmSelectedOuterNode);

  const onClick = () => {
    if (isEditing) {
      dispatch(activateModal({ id: "MapImagePicker" }));
    }
  };

  const image = outerNode === undefined ? (localImage ?? topic?.images?.thumbnail) : undefined;

  let overlay = overlayElement;
  if (isEditing) {
    overlay = (
      <ThemeProvider theme={darkTheme}>
        <StyledCentralContainer>
          {image ? (
            <UIButton variant="outlined">{translate("app.create.change_image")}</UIButton>
          ) : (
            <UIButton variant="filled">{translate("app.create.add_image")}</UIButton>
          )}
        </StyledCentralContainer>
      </ThemeProvider>
    );
  }

  return (
    <StyledContainer isEditing={isEditing}>
      <StyledImage
        advancedMode={!!outerNode}
        dataTestId="pick-a-custom-map-image-button"
        isEditing={isEditing}
        onClick={onClick}
        src={image}
      />
      <StyledInnerContainer hasImage={!!image} isEditing={isEditing}>
        {topic && outerNode ? (
          <TopicConnectionBanner
            title={topic.name}
            secondaryTitle={outerNode.name}
            imageUrl={topic.images?.x_small}
            secondaryImageUrl={outerNode.images?.x_small}
          />
        ) : (
          overlay
        )}
      </StyledInnerContainer>
    </StyledContainer>
  );
};
